import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { useCoachingGuide } from '@/hooks/coaching/useCoachingGuide'
import { useWorkbookGrades } from '@/hooks/coaching/useWorkbookGrades'
import {
  useAdvanceLesson,
  useCompleteLesson,
  useLessonCompletionReadiness,
} from '@/hooks/coaching/useSessionControl'
import type { ComponentProgressDetailView, LessonProcessDetailView, WorkbookMode } from '@/types/coaching'
import { ConfirmDialog } from './ConfirmDialog'
import {
  incompleteEssentialChecks,
  incompleteWorkbookProblemNos,
  isRequiredBuildupGradeItem,
} from './lessonOperationStatus'

interface Props {
  learnerId: number
  lesson: LessonProcessDetailView
  attendanceOpen?: boolean
  workbookMode?: WorkbookMode
}

type DialogMode = 'complete' | 'force'
type Translate = (key: string, options?: Record<string, unknown>) => string

const SERVER_REQUIREMENT_TRANSLATION_KEYS = {
  COMPONENTS: 'lessonComplete.missing.components',
  BUILDUP_GRADES: 'lessonComplete.missing.buildupGrades',
  BLOCK_QUIZ_RESPONSES: 'lessonComplete.missing.blockQuizResponses',
  ESSENTIAL_CHECKS: 'lessonComplete.missing.essentialChecks',
  WORKBOOK_GRADES: 'lessonComplete.missing.workbookGrades',
} as const

type ServerRequirementCode = keyof typeof SERVER_REQUIREMENT_TRANSLATION_KEYS

function isServerRequirementCode(value: unknown): value is ServerRequirementCode {
  return typeof value === 'string'
    && Object.prototype.hasOwnProperty.call(SERVER_REQUIREMENT_TRANSLATION_KEYS, value)
}

function apiErrorCode(e: unknown): string | null {
  if (axios.isAxiosError(e)) return e.response?.data?.error?.code ?? null
  return null
}

function apiIncompleteRequirementCodes(e: unknown): ServerRequirementCode[] {
  if (!axios.isAxiosError(e)) return []
  const data = e.response?.data as {
    error?: { details?: { incompleteRequirements?: unknown } }
  } | undefined
  const requirements = data?.error?.details?.incompleteRequirements
  return Array.isArray(requirements) ? requirements.filter(isServerRequirementCode) : []
}

export function LessonCompleteControl({
  learnerId,
  lesson,
  attendanceOpen = true,
  workbookMode = 'IMMEDIATE',
}: Props) {
  const { t } = useTranslation(['coaching', 'common'])
  const completionAvailable = lesson.lessonKind === 'designated_review'
    ? lesson.reviewAttemptCompleted !== true
    : lesson.status !== 'completed'
  const isDesignatedReview = lesson.lessonKind === 'designated_review' && completionAvailable
  const learningAttemptId = isDesignatedReview ? (lesson.reviewAttemptId ?? null) : null
  const activeComponents = isDesignatedReview && lesson.reviewComponents?.length
    ? lesson.reviewComponents
    : lesson.components
  const lessonForCompletion = activeComponents === lesson.components
    ? lesson
    : { ...lesson, components: activeComponents }
  const guide = useCoachingGuide(
    learnerId,
    lesson.lessonId,
    learningAttemptId,
    attendanceOpen && completionAvailable,
  )
  const workbookEnabled = !isDesignatedReview && lesson.workbookGradingEnabled !== false
  const workbook = useWorkbookGrades(
    learnerId,
    lesson.lessonId,
    attendanceOpen && workbookEnabled && completionAvailable,
  )
  const readiness = useLessonCompletionReadiness(learnerId, lesson.lessonId, workbookMode)
  const complete = useCompleteLesson(learnerId, lesson.lessonId, workbookMode)
  const advance = useAdvanceLesson(learnerId)
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [serverIncompleteRequirementCodes, setServerIncompleteRequirementCodes] = useState<ServerRequirementCode[]>([])

  if (!completionAvailable) return null

  const metadataLoading = guide.isLoading || (workbookEnabled && workbook.isLoading)
  const incompleteRequirements = listIncompleteRequirementNames(
    lessonForCompletion,
    guide.data?.essentialChecks ?? [],
    workbookEnabled && workbookMode === 'IMMEDIATE' ? (workbook.data ?? []) : [],
    workbookMode === 'IMMEDIATE',
    t,
  )

  const handleClick = async () => {
    setError(null)
    setServerIncompleteRequirementCodes([])
    try {
      const result = await readiness.mutateAsync()
      const requirementCodes = result.incompleteRequirements.filter(isServerRequirementCode)
      setServerIncompleteRequirementCodes(requirementCodes)
      setDialogMode(result.ready ? 'complete' : 'force')
    } catch (e) {
      const code = apiErrorCode(e)
      setDialogMode(null)
      setError(code ? t(`common:apiError.${code}`, t('common:error.unknown')) : t('common:error.unknown'))
    }
  }

  const handleConfirm = async () => {
    const force = dialogMode === 'force'
    try {
      await complete.mutateAsync(force)
      if (!isDesignatedReview) {
        await advance.mutateAsync(false)
      }
      setServerIncompleteRequirementCodes([])
      setDialogMode(null)
    } catch (e) {
      const code = apiErrorCode(e)
      if (code === 'REVIEW_NOTE_COMPLETION_REQUIRED') {
        setDialogMode(null)
        setError(t('common:apiError.REVIEW_NOTE_COMPLETION_REQUIRED', t('common:error.unknown')))
        return
      }
      if (!force && code === 'LESSON_NOT_COMPLETE') {
        setServerIncompleteRequirementCodes(apiIncompleteRequirementCodes(e))
        setDialogMode('force')
        return
      }
      setDialogMode(null)
      setError(code ? t(`common:apiError.${code}`, t('common:error.unknown')) : t('common:error.unknown'))
    }
  }

  const isForce = dialogMode === 'force'
  const pending = readiness.isPending || complete.isPending || (!isDesignatedReview && advance.isPending)
  const serverIncompleteRequirements = serverIncompleteRequirementCodes.map(
    (code) => t(
      code === 'WORKBOOK_GRADES' && workbookMode === 'IMMEDIATE'
        ? 'lessonComplete.missing.workbookGradesNextClass'
        : SERVER_REQUIREMENT_TRANSLATION_KEYS[code],
    ),
  )
  const forceWarningItems = serverIncompleteRequirements.length > 0
    ? serverIncompleteRequirements
    : incompleteRequirements.length > 0
      ? incompleteRequirements
      : [t('lessonComplete.missing.serverDetected')]

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={!attendanceOpen || metadataLoading || pending}
        title={!attendanceOpen ? t('lessonComplete.attendanceRequired') : undefined}
        className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400"
      >
        {t('lessonComplete.button')}
      </button>
      {!attendanceOpen ? (
        <p className="text-xs text-gray-500">{t('lessonComplete.attendanceRequired')}</p>
      ) : null}
      {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
      <ConfirmDialog
        open={dialogMode != null}
        title={isForce ? t('lessonComplete.forceTitle') : t('lessonComplete.confirmTitle')}
        message={isForce ? t('lessonComplete.forceMessage') : t('lessonComplete.confirmMessage')}
        confirmLabel={isForce ? t('lessonComplete.forceConfirm') : t('lessonComplete.confirmLabel')}
        cancelLabel={t('lessonComplete.cancel')}
        pending={pending}
        tone={isForce ? 'danger' : 'primary'}
        variant={isForce ? 'warning' : 'default'}
        warningItemsLabel={isForce ? t('lessonComplete.forceItemsLabel') : undefined}
        warningItems={isForce ? forceWarningItems : undefined}
        onCancel={() => {
          setServerIncompleteRequirementCodes([])
          setDialogMode(null)
        }}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

function listIncompleteRequirementNames(
  lesson: LessonProcessDetailView,
  essentialChecks: Array<{ checkNo?: number; label?: string | null; score?: number | null }>,
  workbookGrades: Array<{ itemKind: string; itemNo: number; isCorrect?: boolean | null }>,
  requireWorkbookGrades: boolean,
  t: Translate,
): string[] {
  const names = lesson.components
    .filter((component) => component.status !== 'completed')
    .map((component) => component.label?.trim() || t('lessonComplete.missing.components'))

  if (countIncompleteBuildupGrades(lesson.components) > 0) {
    names.push(t('lessonComplete.missing.buildupGrades'))
  }
  if (countIncompleteBlockQuizResponses(lesson.components) > 0) {
    names.push(t('lessonComplete.missing.blockQuizResponses'))
  }
  if (incompleteEssentialChecks(essentialChecks).length > 0) {
    names.push(t('lessonComplete.missing.essentialChecks'))
  }
  if (requireWorkbookGrades && incompleteWorkbookProblemNos(
    workbookGrades,
    lesson.workbookGradingEnabled !== false,
  ).length > 0) {
    names.push(t('lessonComplete.missing.workbookGradesNextClass'))
  }

  return [...new Set(names)]
}

function countIncompleteBuildupGrades(components: ComponentProgressDetailView[]): number {
  return components
    .filter((component) => component.type === 'buildup')
    .flatMap((component) => {
      const items = component.buildup?.steps.flatMap((step) =>
        step.items.filter((item) => isRequiredBuildupGradeItem(step.step, item.number))) ?? []
      return items.filter((item) => !item.grade)
    })
    .length
}

function countIncompleteBlockQuizResponses(components: ComponentProgressDetailView[]): number {
  return components
    .filter((component) => component.type === 'video' && component.video?.pauseEnabled)
    .flatMap((component) => {
      const points = component.video?.pausePoints ?? []
      const responses = component.video?.pauseQuizResponses ?? []
      const completed = new Set(
        responses
          .filter((response) => response.isCorrect !== null && response.isCorrect !== undefined)
          .map((response) => `${response.quizId}:${response.pauseTimeMs}`),
      )
      return points.filter((point) => !completed.has(`${point.quizId}:${point.pauseTimeMs}`))
    })
    .length
}
